import {ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, EventEmitter, Injectable, Input, Output, signal} from "@angular/core";
import {AgendaelementId, AgendaelementModell, OppdaterPauseSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable} from "rxjs";
import {Temporal} from "temporal-polyfill";
import {SakstidspunkterBeregning} from "felles-ng2/tjenester/Sakstidspunkter";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {Skedulering} from "felles-ng2/støttefunksjoner/timeout";
import {scrollTilTopp} from "web-ng2/delt/dom/scrollIntoView";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {sakStarttidspunkt, startklokkeslettSakSomUTC} from "felles-ng2/modell/sakModellHjelpere";
import {erFlerdagsmøte, erMøteAvholdt} from "felles-ng2/modell/møteModellHjelpere";
import {durationFraMinutter, durationTilMinutter} from "felles-ng2/støttefunksjoner/temporal";
import {konsumerTilDestroy} from "felles-ng2/infrastruktur/rxjs/observerbarHjelpere";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {filterNil} from "@ngneat/elf";
import {DataOgMeta, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {KlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/klokkeslett-velger/klokkeslett-velger";
import {MatButton} from "@angular/material/button";
import {MatError, MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TranslateModule} from "@ngx-translate/core";
import {VarighetMinutterVelgerKomponent} from "web-ng2/delt/komponenter/varighet-minutter-velger/varighet-minutter-velger";
import {VelgDagFraFlerdagsmøteKomponent} from "../velg-dag-fra-flerdagsmøte/velg-dag-fra-flerdagsmøte";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import PlainTime = Temporal.PlainTime;
import Duration = Temporal.Duration;
import {tomAgendaelementModell} from "felles-ng2/modell/tommeModeller";

const debug = initDebugger('PauseRedigerKomponent');

/**
 * Rediger pause
 */
@Component({
    selector: 'pause-rediger',
    templateUrl: 'pause-rediger.html',
    styleUrl: 'pause-rediger.scss',
    imports: [
        MatButton,
        MatError,
        MatFormField,
        MatIcon,
        MatInput,
        MatLabel,
        MatPrefix,
        FormsModule,
        InlineSkjemaHandlingerKomponent,
        InlineSkjemaKomponent,
        KlokkeslettVelgerKomponent,
        NavigasjonforhindretFlashDirektiv,
        ReactiveFormsModule,
        StandardValideringsfeilPipe,
        TranslateModule,
        VarighetMinutterVelgerKomponent,
        VelgDagFraFlerdagsmøteKomponent,
        VisSpinnerDirektiv,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PauseRedigerKomponent {
    static ajsSelector = 'pauseRediger';

    @Output() vedLukk = new EventEmitter<void>();

    /** Pause-id, mer at vi håndterer ikke endring i angitt id */
    @Input({required: true}) set id(id: AgendaelementId) {
        this.lastPause(id);
    }
    @Input({required: true}) sakstidspunkter!: SakstidspunkterBeregning;

    skjema = this.formBuilder.group({
        tittel: ['', FeltValidatorer.litenIkkeTomStreng],
        varighet: [<Duration | null>null],
        beskrivelse: ['', FeltValidatorer.htmlTekst],
        //kun relevante ved flerdagsmøter
        dag: [<number | null>null],
        startklokkeslett: [<PlainTime | null>null],
    })

    pause = signal<AgendaelementModellDLM>(tomAgendaelementModell() as AgendaelementModellDLM);
    erAvholdt = computed(() => erMøteAvholdt(this.pause().moete, this.klokke));
    starttidspunkt!: PlainTime | null;

    lagrer = asynkArbeidPågårSignal();

    //flerdagsmøte-relatert
    erFlerdagsmoete = computed(() => erFlerdagsmøte(this.pause().moete));
    visStartklokkeslettvelger = false;

    private initialisert = false;

    constructor(private avhengigheter: PauseRedigerKomponentAvhengigheter,
                private element: ElementRef<HTMLElement>,
                private gjeldendeTidssone: GjeldendeTidssone,
                private klokke: Klokke,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private formBuilder: FormBuilder,
                private destroyRef: DestroyRef) {
        this.navigasjonDeaktivering.deaktiverNavigasjon();
    }

    async lagre() {
        const spesifikasjon = this.somSpesifikasjon();
        await this.lagrer.utfør(() => this.avhengigheter.lagre(this.pause().id, spesifikasjon));
        this.vedLukk.emit();
    }

    avbryt() {
        this.vedLukk.emit();
    }

    ngAfterViewInit() {
        //scroll skjemaet, men med en liten forsinkelse slik at det har rukket å oppta vertikal plass
        new Skedulering(10, 'scroll', this.destroyRef)
          .skeduler(() => scrollTilTopp(this.element.nativeElement));
    }

    ngOnDestroy(): void {
        this.sakstidspunkter.resetOverstyringer();
        this.navigasjonDeaktivering.reaktiverNavigasjon();
    }

    // private hjelpemetoder

    private lastPause(id: AgendaelementId) {
        this.skjema.disable();
        this.avhengigheter.pause(id)
          .pipe(terminerEtterFørsteRequestRespons())
          .subscribe(sakOgMeta => {
              this.pause.set(sakOgMeta.data);
              this.populerFraPause();
              if (!sakOgMeta.meta.levertFraCache) this.skjema.enable();
          });
    }

    private populerFraPause() {
        this.starttidspunkt = sakStarttidspunkt(this.pause())?.toPlainTime() ?? null;

        //flerdagsmøte-relatert
        if (this.erFlerdagsmoete()) {
            this.skjema.controls.dag.addValidators(Validators.required);
            this.dagEndret(this.pause().dag!); //for at visStartklokkeslettvelger skal settes
        }

        this.oppdaterSkjemaFraPause();

        //populerFraPause kan kalles multiple ganger - sørg for at følgende init kun skjer én gang
        if (this.initialisert) {
            this.registrerLyttere();
            this.initialisert = true;
        }
    }

    /** Dersom bruker endrer dag må starttidspunktvelger vises,
     * med mindre bruker endrer til dag 0 - da bestemmes starttidspunktet av møte.start*/
    private dagEndret(valgtDag: number) {
        //startklokkeslett styres av møte.dato.start for dag 0
        if (valgtDag == 0) {
            this.visStartklokkeslettvelger = false;
            return
        }
        //dersom dag ikke er endret - avgjør basert på om pause.starttidspunkt er satt
        if (valgtDag == this.pause().dag)
            this.visStartklokkeslettvelger = erIkkeNil(this.pause().starttidspunkt);
        else
            this.visStartklokkeslettvelger = true; //vi vil alltid vise startklokkeslett-felt dersom bruker endrer til annen dag enn 0
    }

    private startklokkeslettEndret(valgtKlokkeslett: PlainTime | null) {
        this.starttidspunkt = valgtKlokkeslett;
    }

    private varighetEndret(valgtVarighet: Duration | null) {
        //propagerer endring av varighet til sakstidspunkter slik at eventuell møte-varighet-overskridelse vises i møte.ts
        this.sakstidspunkter.medOverstyringForSak({...this.pause(), varighet: durationTilMinutter(valgtVarighet)});
    }

    private oppdaterSkjemaFraPause() {
        this.skjema.patchValue({
            tittel: this.pause().tittel,
            varighet: durationFraMinutter(this.pause().varighet),
            beskrivelse: this.pause().beskrivelse,

            //flerdagsmøte-relatert
            dag: this.pause().dag,
            startklokkeslett: sakStarttidspunkt(this.pause())?.toPlainTime() ?? null
        });
    }

    private somSpesifikasjon(): OppdaterPauseSpesifikasjon {
        const skjemaverdier = this.skjema.value;
        return {
            tittel: skjemaverdier.tittel!,
            beskrivelse: skjemaverdier.beskrivelse!,
            dag: skjemaverdier.dag ?? null,
            //server forventer klokkeslett i UTC
            startklokkeslett: startklokkeslettSakSomUTC(skjemaverdier.startklokkeslett, skjemaverdier.dag, this.pause().moete, this.gjeldendeTidssone.brukersTidssone())?.toJSON() ?? null,
            varighet: erIkkeNil(skjemaverdier.varighet) ? durationTilMinutter(skjemaverdier.varighet) : null,
        };
    }

    private registrerLyttere() {
        this.lytt(this.skjema.controls.varighet.valueChanges, verdi => this.varighetEndret(verdi));
        if (this.erFlerdagsmoete()) {
            this.lytt(this.skjema.controls.dag.valueChanges, verdi => this.dagEndret(verdi!));
            this.lytt(this.skjema.controls.startklokkeslett.valueChanges, verdi => this.startklokkeslettEndret(verdi));
        }
    }

    private lytt<T>(observerbar: Observable<T>, vedData: (mottatt: T) => void) {
        konsumerTilDestroy(observerbar, this.destroyRef, vedData);
    }
}

@Injectable()
export class PauseRedigerKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste) {
    }

    pause(id: AgendaelementId): Observable<DataOgMeta<AgendaelementModellDLM>> {
        return this.sakDatatjeneste.hentMedMeta(id).pipe(filterNil());
    }

    async lagre(id: AgendaelementId, spesifikasjon: OppdaterPauseSpesifikasjon): Promise<void> {
        await this.sakDatatjeneste.oppdaterPause(id, spesifikasjon);
    }

}
